/**
 * Building the provider cards the settings page renders.
 *
 * Lifted out of `ProviderSettingsPanel` so the component holds hooks and
 * layout rather than a 97-line view-model derivation. It takes nine values it
 * used to close over, which is a narrow enough interface to be worth the file
 * boundary: the alternative was a component where the data shaping and the
 * rendering were separated only by a blank line.
 *
 * Pure — no hooks, no side effects. Everything it needs arrives as input, so
 * the card shape can be asserted without mounting anything.
 *
 * @module
 */
import { ProviderKind, ServerProvider, ServerProviderModel } from "@cmmd-forge/contracts";
import { DEFAULT_UNIFIED_SETTINGS, type UnifiedSettings } from "@cmmd-forge/contracts/settings";

import { isSettingsProviderComingSoon } from "../../providerPickerAvailability";
import { isCloudProviderSignInEnabled } from "../../hooks/useCloudProviderSignIn";
import { Equal } from "effect";
import { formatCodexPlanLabel, resolveCodexAccountIdentity } from "./codexAuthViewModel";
import {
  CmmdProviderAccountInventoryAccount,
  resolveDefaultProviderAccountRow,
  resolveProviderAccountRowSource,
  resolveProviderAccountRows,
} from "./providerAccountState";
import { ProviderAccountListItem } from "./ProviderAccountManagePanel";
import { PROVIDER_STATUS_STYLES } from "./providerAuthCopy";
import type { ProviderCardModel } from "./providerCardModel";
import { isDebugDisclosureEnabled } from "./providerProvenance";
import {
  getProviderVersionLabel,
  resolveProviderCardSummary,
  resolveProviderStatusKey,
} from "./providerSummary";
import type { PROVIDER_SETTINGS } from "./providerSettingsCatalog";
import type { CmmdCodexCredentialState } from "../../environments/cmmdApi";

// moved verbatim from the 5377-line SettingsPanels.tsx, then again from
// ProviderSettingsPanel; complexity is unchanged, and fallow reads a relocated
// function as a new one
// fallow-ignore-next-line complexity
function resolveProviderCardDefaultAccount(input: {
  readonly provider: ProviderKind;
  readonly liveProvider: ServerProvider | undefined;
  readonly codexProviderAccounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  readonly claudeProviderAccounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  readonly codexCloudCredential: CmmdCodexCredentialState | null | undefined;
}): ProviderAccountListItem | null {
  if (input.provider !== "codex" && input.provider !== "claudeAgent") return null;

  const codexIdentity =
    input.provider === "codex"
      ? resolveCodexAccountIdentity(input.liveProvider, input.codexCloudCredential)
      : null;
  const accountInventory =
    input.provider === "codex" && codexIdentity !== null
      ? resolveProviderAccountRowSource({
          providerAccounts: input.codexProviderAccounts,
          connectedViaCloudStore: codexIdentity.connectedViaCloudStore,
          hasLocalAccount: codexIdentity.hasUserCodexAccount,
        })
      : input.claudeProviderAccounts;

  return resolveDefaultProviderAccountRow(
    resolveProviderAccountRows({
      providerAccounts: accountInventory,
      accountEmail:
        codexIdentity?.codexAccountLabel ??
        input.liveProvider?.auth.accountEmail ??
        input.liveProvider?.auth.provenance?.accountLabel ??
        null,
      planLabel: formatCodexPlanLabel(
        codexIdentity?.codexSubscriptionLabel ?? input.liveProvider?.auth.label,
      ),
      isDefaultForNewChats: true,
      needsReconnect: codexIdentity?.needsReconnect ?? false,
    }),
  );
}

export interface BuildProviderCardsInput {
  readonly visibleProviderSettings: ReadonlyArray<(typeof PROVIDER_SETTINGS)[number]>;
  readonly serverProviders: ReadonlyArray<ServerProvider>;
  readonly settings: UnifiedSettings;
  readonly orgProviderPolicy: NonNullable<
    Parameters<typeof isSettingsProviderComingSoon>[0]["policy"]
  >;
  readonly codexProviderAccounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  readonly claudeProviderAccounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>;
  readonly codexCloudCredentialConnected: boolean;
  readonly claudeCloudCredentialConnected: boolean;
  readonly codexCloudSignIn: {
    readonly connection: CmmdCodexCredentialState | null;
  };
}

/** One card per provider the current policy makes visible. */
// Moved verbatim from ProviderSettingsPanel, which moved it verbatim from the
// 5377-line SettingsPanels.tsx. Complexity is unchanged, and fallow reads a
// relocated function as a new one.
// fallow-ignore-next-line complexity
export function buildProviderCards(
  input: BuildProviderCardsInput,
): ReadonlyArray<ProviderCardModel> {
  const {
    visibleProviderSettings,
    serverProviders,
    settings,
    orgProviderPolicy,
    codexProviderAccounts,
    claudeProviderAccounts,
    codexCloudCredentialConnected,
    claudeCloudCredentialConnected,
    codexCloudSignIn,
  } = input;
  return visibleProviderSettings.map((providerSettings) => buildOneCard(providerSettings));

  // Moved verbatim: 5377-line SettingsPanels.tsx -> ProviderSettingsPanel ->
  // here. Complexity is unchanged by the relocation; fallow reads a moved
  // function as a new one. The original carried this same suppression.
  // fallow-ignore-next-line complexity
  function buildOneCard(providerSettings: (typeof PROVIDER_SETTINGS)[number]): ProviderCardModel {
    const liveProvider = serverProviders.find(
      (candidate) =>
        candidate.provider ===
        (providerSettings.provider === "apex" ? "cmmd" : providerSettings.provider),
    );
    const isComingSoon = isSettingsProviderComingSoon({
      provider: providerSettings.provider,
      policy: orgProviderPolicy,
      isDebugDisclosureEnabled: isDebugDisclosureEnabled(),
    });
    const providerConfig = settings.providers[providerSettings.provider];
    const defaultProviderConfig = DEFAULT_UNIFIED_SETTINGS.providers[providerSettings.provider];
    const hasPersonalCredential =
      providerSettings.provider === "codex"
        ? codexCloudCredentialConnected
        : providerSettings.provider === "claudeAgent"
          ? claudeCloudCredentialConnected
          : false;
    const statusKey = resolveProviderStatusKey({
      provider: providerSettings.provider,
      providerEnabled: providerConfig.enabled,
      isComingSoon,
      liveProvider,
      codexAccessMode: settings.providers.codex.accessMode,
      hasPersonalCredential,
    });
    const summary = resolveProviderCardSummary({
      provider: providerSettings.provider,
      liveProvider,
      isComingSoon,
      isDebugDisclosureEnabled: isDebugDisclosureEnabled(),
      codexAccessMode: settings.providers.codex.accessMode,
      hasPersonalCredential,
    });
    const models: ReadonlyArray<ServerProviderModel> = liveProvider?.models ?? [];
    const defaultAccount = resolveProviderCardDefaultAccount({
      provider: providerSettings.provider,
      liveProvider,
      codexProviderAccounts,
      claudeProviderAccounts,
      codexCloudCredential: isCloudProviderSignInEnabled() ? codexCloudSignIn.connection : null,
    });

    if (providerSettings.kind === "cli") {
      const card: ProviderCardModel = {
        kind: "cli",
        provider: providerSettings.provider,
        title: providerSettings.title,
        isDirty: !Equal.equals(providerConfig, defaultProviderConfig),
        liveProvider,
        models,
        providerConfig,
        statusStyle: PROVIDER_STATUS_STYLES[statusKey],
        summary,
        hasPersonalCredential,
        defaultAccount,
        versionLabel: getProviderVersionLabel(liveProvider?.version),
        isComingSoon,
        binaryPathValue: (providerConfig as { binaryPath: string }).binaryPath,
        binaryPlaceholder: providerSettings.binaryPlaceholder,
        binaryDescription: providerSettings.binaryDescription,
        // Spread rather than assigned after the fact: the model's fields are
        // readonly, and under `exactOptionalPropertyTypes` an absent optional
        // is not the same as one set to undefined. Each stays independently
        // conditional, exactly as the three `if` statements were.
        ...(providerSettings.homePathKey === undefined
          ? {}
          : { homePathKey: providerSettings.homePathKey }),
        ...(providerSettings.homePlaceholder === undefined
          ? {}
          : { homePlaceholder: providerSettings.homePlaceholder }),
        ...(providerSettings.homeDescription === undefined
          ? {}
          : { homeDescription: providerSettings.homeDescription }),
      };
      return card;
    }
    return {
      kind: "http",
      provider: providerSettings.provider,
      title: providerSettings.title,
      isDirty: !Equal.equals(providerConfig, defaultProviderConfig),
      liveProvider,
      models,
      providerConfig,
      statusStyle: PROVIDER_STATUS_STYLES[statusKey],
      summary,
      hasPersonalCredential,
      defaultAccount,
      versionLabel: getProviderVersionLabel(liveProvider?.version),
      isComingSoon,
      endpointUrl: (providerConfig as { baseUrl: string }).baseUrl,
      endpointPlaceholder: providerSettings.endpointPlaceholder,
      endpointDescription: providerSettings.endpointDescription,
    };
  }
}
